/** @jsx jsx */
import React, { useCallback } from 'react';
import { styled, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import type { DropdownMenuProps, OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { UploadPermissions } from '@atlassian/jira-issue-navigator-issue-operations-services/src/services/media-upload-permissions/types.tsx';
import type {
	DataState,
	Operation,
	IssueLink,
} from '@atlassian/jira-issue-navigator-issue-operations-services/src/services/operations-and-actions/index.tsx';
import {
	APPROVE_APPROVAL,
	REJECT_APPROVAL,
	SKIP_FIELDS_OLD,
	SKIP_FIELDS_NEW,
	URL_OVERRIDE_FIELDS,
	DIALOG_FIELDS,
	OPERATION_IDS,
} from '@atlassian/jira-issue-operations-commons/src/constants.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useRouter } from '@atlassian/react-resource-router';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ModalDropdownItemTrigger } from '@atlassian/entry-point-modal-trigger/ModalDropdownItemTrigger';
import { attachFileDialogEntryPoint } from '@atlassian/jira-issue-operations-attach-file-dialog/src/entrypoint.tsx';
import { ErrorState } from '../../common/ui/error-state/index.tsx';
import { DropdownButtonItem } from './button-item/index.tsx';
import messages from './messages.tsx';
import { RouteLinkItem, RouteLinkItemOld } from './route-link-item/index.tsx';

export type Props = {
	issueId: number;
	issueKey: string;
	workType?: string | null;
	dataState: DataState;
	uploadPermissions: UploadPermissions;
	onIssueRefetch: () => void;
	onIssueDelete: () => void;
	onIssueDeleteFn: ((issueKey: string) => void) | undefined;
	onRankToTop: (onSuccess: () => void, onFailure: () => void) => void;
	onRankToBottom: (onSuccess: () => void, onFailure: () => void) => void;
	onApprove: (onSuccess: () => void, onFailure: () => void) => void;
	onReject: (onSuccess: () => void, onFailure: () => void) => void;
	elementToFocusOnClose?: string;
	isCreateSubTaskAllowed?: boolean;
};

const isUrlOverrideFieldClass = (
	className: string,
): className is keyof typeof URL_OVERRIDE_FIELDS =>
	Object.keys(URL_OVERRIDE_FIELDS).includes(className);

const getDialogFields = () => [...DIALOG_FIELDS, APPROVE_APPROVAL, REJECT_APPROVAL];

const mapOperationToActionItem = ({ name, url: givenUrl, styleClass: field }: Operation) => {
	const url = isUrlOverrideFieldClass(field) ? URL_OVERRIDE_FIELDS[field] : givenUrl;

	return { name, url, field };
};

const linksNeedToSkip = ({ id }: IssueLink) =>
	fg('nin_add_link_issue_to_issue_operations')
		? !SKIP_FIELDS_NEW.includes(id)
		: !SKIP_FIELDS_OLD.includes(id);

export const DropdownMenuGroupNew = ({
	issueId,
	dataState,
	uploadPermissions,
	isCreateSubTaskAllowed,
	workType,
	onIssueDeleteFn,
	onIssueDelete,
	...props
}: Props): JSX.Element => {
	const { issueKey } = props;
	const { data, loadFieldResources } = dataState;
	const { permissions, error: permissionsError } = uploadPermissions;
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ route }] = useRouter();
	const isServiceDeskList = route?.name === 'servicedesk-list';

	const fireAnalyticsEvent = useCallback(
		(fieldType: string) => {
			const analyticsEvent = createAnalyticsEvent({ action: 'clicked', actionSubject: 'field' });
			fireUIAnalytics(analyticsEvent, 'meatballField', { fieldType });
		},
		[createAnalyticsEvent],
	);

	const skipFields = fg('nin_add_link_issue_to_issue_operations')
		? SKIP_FIELDS_NEW
		: SKIP_FIELDS_OLD;

	const operationsNeedToSkip = useCallback(
		({ styleClass: field }: Operation) =>
			!skipFields.includes(field) &&
			!(field === OPERATION_IDS.ATTACH_FILE && permissionsError) &&
			!(field === OPERATION_IDS.CREATE_SUBTASK && !isCreateSubTaskAllowed),
		[isCreateSubTaskAllowed, permissionsError, skipFields],
	);

	const isIssueArchivalEnabled =
		fg('issue_deletion_two_step_process_free_standard') &&
		data?.operations
			?.filter(operationsNeedToSkip)
			.map(mapOperationToActionItem)
			.some(({ field }) => field !== null && field === OPERATION_IDS.ARCHIVE);

	const createButtonItem = (name: string, url: string, field: string) => (
		<ListItem key={field}>
			<DropdownButtonItem
				issueId={issueId}
				url={url}
				workType={workType}
				name={name}
				styleClass={field}
				onAddFieldClick={loadFieldResources}
				uploadPermissions={permissions}
				onFireAnalyticsEvent={fireAnalyticsEvent}
				isIssueArchivalEnabled={isIssueArchivalEnabled}
				onAddFlag={noop}
				onIssueDelete={expVal('child-issue-archival-ga', 'isEnabled', false) ? noop : onIssueDelete}
				onIssueDeleteFn={
					expVal('child-issue-archival-ga', 'isEnabled', false) ? onIssueDeleteFn : noop
				}
				{...props}
			/>
		</ListItem>
	);

	return (
		<DropdownMenuHeight>
			<DropdownItemGroup testId="issue-navigator-issue-operations.ui.dropdown-menu-group.menu-container">
				<List>
					<ListItem>
						{isServiceDeskList && fg('jsm_list_view_milestone_1') ? (
							<RouteLinkItem
								key={OPERATION_IDS.VIEW_ISSUE}
								href={`/browse/${issueKey}`}
								target="_blank"
								onClick={() => fireAnalyticsEvent(OPERATION_IDS.VIEW_ISSUE)}
								params={{ issueKey }}
								menuItemId={OPERATION_IDS.VIEW_ISSUE}
								actionSubjectId="issueOperationsMeatballMenu"
							>
								{formatMessage(
									fg('jira-issue-terminology-update-m4')
										? messages.viewIssueLabelIssueTerminologyRefreshM4
										: messages.viewIssueLabel,
								)}
							</RouteLinkItem>
						) : (
							<RouteLinkItem
								key={OPERATION_IDS.VIEW_ISSUE}
								href={`/browse/${issueKey}`}
								onClick={() => fireAnalyticsEvent(OPERATION_IDS.VIEW_ISSUE)}
								params={{ issueKey }}
								menuItemId={OPERATION_IDS.VIEW_ISSUE}
								actionSubjectId="issueOperationsMeatballMenu"
							>
								{formatMessage(
									fg('jira-issue-terminology-update-m4')
										? messages.viewIssueLabelIssueTerminologyRefreshM4
										: messages.viewIssueLabel,
								)}
							</RouteLinkItem>
						)}
					</ListItem>

					{data?.operations
						?.filter(operationsNeedToSkip)
						.map(mapOperationToActionItem)
						.map(({ name, url, field }) => {
							const dialogFields = getDialogFields();
							if (dialogFields.includes(field)) {
								if (
									field === OPERATION_IDS.ATTACH_FILE &&
									fg('empanada_nin_convert_issue_ops_to_entrypoint')
								) {
									// Only render if we have valid permissions data
									if (!permissions) {
										return null;
									}

									return (
										<ListItem key={field}>
											<ModalDropdownItemTrigger
												entryPoint={attachFileDialogEntryPoint}
												entryPointProps={{
													issueId,
													issueKey,
													workType,
													mediaUploadContext: permissions,
												}}
												onClick={() => fireAnalyticsEvent(field)}
												testId="issue-navigator-issue-operations.ui.dropdown-menu-group.attach-file-modal-trigger"
											>
												{name}
											</ModalDropdownItemTrigger>
										</ListItem>
									);
								}
								return createButtonItem(name, url, field);
							}

							return fg('nin_trigger_connect_webitem_dialog') ? (
								<ListItem key={field}>
									<RouteLinkItem
										href={url}
										onClick={() => fireAnalyticsEvent(field)}
										styleClass={field}
										name={name}
										menuItemId={name}
										actionSubjectId="issueOperationsMeatballMenu"
									>
										{name}
									</RouteLinkItem>
								</ListItem>
							) : (
								<ListItem key={field}>
									<RouteLinkItemOld
										href={url}
										onClick={() => fireAnalyticsEvent(field)}
										styleClass={field}
										name={name}
									>
										{name}
									</RouteLinkItemOld>
								</ListItem>
							);
						})}

					{data?.links
						?.filter(linksNeedToSkip)
						.map(({ id, url, label }) => createButtonItem(label, url, id))}
				</List>
			</DropdownItemGroup>
		</DropdownMenuHeight>
	);
};

export type PropsOld = DropdownMenuProps & {
	issueId: number;
	issueKey: string;
	workType?: string | null;
	dataState: DataState;
	uploadPermissions: UploadPermissions;
	onIssueRefetch: () => void;
	onIssueDelete: () => void;
	onIssueDeleteFn: ((issueKey: string) => void) | undefined;
	onClose: () => void;
	onOpenChange: (args: OnOpenChangeArgs) => void;
	onAddFlag: (styleClass: string, isSuccessful: boolean) => void;
	onRetry: () => void;
	onRankToTop: (onSuccess: () => void, onFailure: () => void) => void;
	onRankToBottom: (onSuccess: () => void, onFailure: () => void) => void;
	onApprove: (onSuccess: () => void, onFailure: () => void) => void;
	onReject: (onSuccess: () => void, onFailure: () => void) => void;
	testId?: string;
	elementToFocusOnClose?: string;
	isCreateSubTaskAllowed?: boolean;
};

export const DropdownMenuGroupOld = ({
	issueId,
	isOpen,
	placement,
	trigger,
	dataState,
	onClose,
	onOpenChange,
	onRetry,
	uploadPermissions,
	testId,
	isCreateSubTaskAllowed,
	workType,
	onIssueDeleteFn,
	onIssueDelete,
	...props
}: PropsOld): JSX.Element => {
	const { issueKey } = props;
	const { data, error, loading: operationsLoading, loadFieldResources } = dataState;
	const { permissions, loading: permissionsLoading, error: permissionsError } = uploadPermissions;
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ route }] = useRouter();
	const isServiceDeskList = route?.name === 'servicedesk-list';

	const fireAnalyticsEvent = useCallback(
		(fieldType: string) => {
			const analyticsEvent = createAnalyticsEvent({ action: 'clicked', actionSubject: 'field' });
			fireUIAnalytics(analyticsEvent, 'meatballField', { fieldType });
		},
		[createAnalyticsEvent],
	);

	const skipFields = fg('nin_add_link_issue_to_issue_operations')
		? SKIP_FIELDS_NEW
		: SKIP_FIELDS_OLD;

	const operationsNeedToSkip = useCallback(
		({ styleClass: field }: Operation) =>
			!skipFields.includes(field) &&
			!(field === OPERATION_IDS.ATTACH_FILE && permissionsError) &&
			!(field === OPERATION_IDS.CREATE_SUBTASK && !isCreateSubTaskAllowed),
		[isCreateSubTaskAllowed, permissionsError, skipFields],
	);

	const isIssueArchivalEnabled =
		fg('issue_deletion_two_step_process_free_standard') &&
		data?.operations
			?.filter(operationsNeedToSkip)
			.map(mapOperationToActionItem)
			.some(({ field }) => field !== null && field === OPERATION_IDS.ARCHIVE);

	const createButtonItem = useCallback(
		(name: string, url: string, field: string) => (
			<ListItem key={field}>
				<DropdownButtonItem
					issueId={issueId}
					url={url}
					workType={workType}
					name={name}
					styleClass={field}
					onAddFieldClick={loadFieldResources}
					onClose={onClose}
					uploadPermissions={permissions}
					onFireAnalyticsEvent={fireAnalyticsEvent}
					isIssueArchivalEnabled={isIssueArchivalEnabled}
					onIssueDelete={
						expVal('child-issue-archival-ga', 'isEnabled', false) ? noop : onIssueDelete
					}
					onIssueDeleteFn={
						expVal('child-issue-archival-ga', 'isEnabled', false) ? onIssueDeleteFn : noop
					}
					{...props}
				/>
			</ListItem>
		),
		[
			fireAnalyticsEvent,
			isIssueArchivalEnabled,
			issueId,
			loadFieldResources,
			onClose,
			onIssueDelete,
			onIssueDeleteFn,
			permissions,
			props,
			workType,
		],
	);

	const createLinkItemNew = useCallback(
		(name: string, url: string, field: string) => (
			<ListItem key={field}>
				<RouteLinkItem
					href={url}
					onClick={() => fireAnalyticsEvent(field)}
					styleClass={field}
					name={name}
					menuItemId={name}
					actionSubjectId="issueOperationsMeatballMenu"
				>
					{name}
				</RouteLinkItem>
			</ListItem>
		),
		[fireAnalyticsEvent],
	);

	const createLinkItemOld = useCallback(
		(name: string, url: string, field: string) => (
			<ListItem key={field}>
				<RouteLinkItemOld
					href={url}
					onClick={() => fireAnalyticsEvent(field)}
					styleClass={field}
					name={name}
				>
					{name}
				</RouteLinkItemOld>
			</ListItem>
		),
		[fireAnalyticsEvent],
	);

	const dropdownMenuProps: DropdownMenuProps = {
		isOpen,
		placement: 'bottom-end',
		shouldRenderToParent: true,
		trigger,
		onOpenChange,
		testId,
		isLoading: operationsLoading || permissionsLoading,
	};

	if (error ?? false) {
		return (
			<DropdownMenu
				{...dropdownMenuProps}
				shouldRenderToParent={fg('should-render-to-parent-should-be-true-empanada')}
			>
				<ErrorState onRetry={onRetry} />
			</DropdownMenu>
		);
	}

	return (
		<DropdownMenu
			{...dropdownMenuProps}
			shouldRenderToParent={fg('should-render-to-parent-should-be-true-empanada')}
		>
			<DropdownMenuHeight>
				<DropdownItemGroup testId="issue-navigator-issue-operations.ui.dropdown-menu-group.menu-container">
					<List>
						<ListItem>
							{isServiceDeskList && fg('jsm_list_view_milestone_1') ? (
								<RouteLinkItem
									key={OPERATION_IDS.VIEW_ISSUE}
									href={`/browse/${issueKey}`}
									target="_blank"
									onClick={() => fireAnalyticsEvent(OPERATION_IDS.VIEW_ISSUE)}
									params={{ issueKey }}
									menuItemId={OPERATION_IDS.VIEW_ISSUE}
									actionSubjectId="issueOperationsMeatballMenu"
								>
									{formatMessage(
										fg('jira-issue-terminology-update-m4')
											? messages.viewIssueLabelIssueTerminologyRefreshM4
											: messages.viewIssueLabel,
									)}
								</RouteLinkItem>
							) : (
								<RouteLinkItem
									key={OPERATION_IDS.VIEW_ISSUE}
									href={`/browse/${issueKey}`}
									onClick={() => fireAnalyticsEvent(OPERATION_IDS.VIEW_ISSUE)}
									params={{ issueKey }}
									menuItemId={OPERATION_IDS.VIEW_ISSUE}
									actionSubjectId="issueOperationsMeatballMenu"
								>
									{formatMessage(
										fg('jira-issue-terminology-update-m4')
											? messages.viewIssueLabelIssueTerminologyRefreshM4
											: messages.viewIssueLabel,
									)}
								</RouteLinkItem>
							)}
						</ListItem>

						{data?.operations
							?.filter(operationsNeedToSkip)
							.map(mapOperationToActionItem)
							.map(({ name, url, field }) => {
								const dialogFields = getDialogFields();
								if (dialogFields.includes(field)) {
									return createButtonItem(name, url, field);
								}

								return fg('nin_trigger_connect_webitem_dialog')
									? createLinkItemNew(name, url, field)
									: createLinkItemOld(name, url, field);
							})}

						{data?.links
							?.filter(linksNeedToSkip)
							.map(({ id, url, label }) => createButtonItem(label, url, id))}
					</List>
				</DropdownItemGroup>
			</DropdownMenuHeight>
		</DropdownMenu>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const List = styled.ul({ padding: '0', margin: '0', listStyle: 'none' });

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListItem = styled.li({ padding: '0', margin: '0', listStyle: 'none' });

/**
 * Work around for https://product-fabric.atlassian.net/browse/DSP-7932
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownMenuHeight = styled.div({
	maxHeight: 312,
	minWidth: 240,
	maxWidth: 240,
});
